import React, { useEffect, useRef, useCallback, useMemo } from "react";
import TreeView from "devextreme-react/tree-view";
import { navigation } from "../../app-navigation";
import { useNavigation } from "../../contexts/navigation";
import { useScreenSize } from "../../utils/media-query";
import "./SideNavigationMenu.scss";
import * as events from "devextreme/events";
import { useAuth } from "../../hooks/useAuth";

/**
 * Recursively filters navigation items while preserving their parent/child
 * structure so child permissions do not expose inaccessible routes.
 *
 * @param {Array} items Configured navigation items.
 * @param {(permission: string) => boolean} canActivate Permission evaluator.
 * @param {boolean} isLarge Whether parent groups should start expanded.
 * @returns {Array} Authorized and normalized navigation items.
 */
export const getAuthorizedNavigationItems = (
	items,
	canActivate,
	isLarge
) =>
	items.reduce((authorizedItems, item) => {
		if (item.permission && !canActivate(item.permission)) {
			return authorizedItems;
		}

		const childItems = item.items
			? getAuthorizedNavigationItems(item.items, canActivate, isLarge)
			: undefined;
		if (item.items && childItems.length === 0) {
			return authorizedItems;
		}

		authorizedItems.push({
			...item,
			expanded: isLarge,
			path:
				item.path && !/^\//.test(item.path)
					? `/${item.path}`
					: item.path,
			...(childItems ? { items: childItems } : {}),
		});
		return authorizedItems;
	}, []);

/**
 * Renders the authorized DevExtreme side-navigation hierarchy.
 *
 * @param {object} props Navigation callbacks and layout state.
 * @returns {JSX.Element} Side-navigation content.
 */
export default function SideNavigationMenu(props) {
	const { canActivate } = useAuth();

	const {
		children,
		selectedItemChanged,
		openMenu,
		compactMode,
		onMenuReady,
	} = props;

	const { isLarge } = useScreenSize();

	const items = useMemo(
		() =>
			getAuthorizedNavigationItems(
				navigation,
				canActivate,
				isLarge
			),
		[canActivate, isLarge]
	);

	const {
		navigationData: { currentPath },
	} = useNavigation();

	const treeViewRef = useRef(null);
	const wrapperRef = useRef();
	const getWrapperRef = useCallback(
		(element) => {
			const prevElement = wrapperRef.current;
			if (prevElement) {
				events.off(prevElement, "dxclick");
			}

			wrapperRef.current = element;
			events.on(element, "dxclick", (e) => {
				openMenu(e);
			});
		},
		[openMenu]
	);

	useEffect(() => {
		const treeView = treeViewRef.current && treeViewRef.current.instance;
		if (!treeView) {
			return;
		}

		if (currentPath !== undefined) {
			treeView.selectItem(currentPath);
			treeView.expandItem(currentPath);
		}

		if (compactMode) {
			treeView.collapseAll();
		}
	}, [currentPath, compactMode]);

	return (
		<div
			className={"dx-swatch-additional side-navigation-menu"}
			ref={getWrapperRef}
		>
			{children}
			<div className={"menu-container"}>
				<TreeView
					ref={treeViewRef}
					items={items}
					keyExpr={"path"}
					selectionMode={"single"}
					focusStateEnabled={false}
					expandEvent={"click"}
					onItemClick={selectedItemChanged}
					onContentReady={onMenuReady}
					width={"100%"}
				/>
			</div>
		</div>
	);
}
